import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from 'react-query';
import { generatePath, useHistory, useParams } from 'react-router-dom';
import useNotifications from 'core/notifications/useNotifications';
import { TemplateManagementRoute } from 'sections/design/template-management/routes';
import { useRcsTemplateEditorDispatch, useRcsTemplateEditorState } from '../context/useRcsTemplateEditorContext';
import { saveRcsTemplate } from '../utils/queries';

/** Hook for saving the RCS template: mutation, redirect URL, and history for exit. */
export function useSaveRcsTemplate() {
  const { t } = useTranslation('designs');
  const { showNotification } = useNotifications();
  const queryClient = useQueryClient();
  const { rcsConfig } = useRcsTemplateEditorState();
  const dispatch = useRcsTemplateEditorDispatch();
  const rcsConfigRef = useRef(rcsConfig);

  const { siteId, id: templateId, language: routeLanguage } = useParams<{
    siteId: string;
    id: string;
    language: string;
  }>();
  const history = useHistory();
  const redirectUrl = generatePath(TemplateManagementRoute.HOME, { siteId, channel: 'rcs' });

  useEffect(() => {
    rcsConfigRef.current = rcsConfig;
  }, [rcsConfig]);

  const saveMutation = useMutation(saveRcsTemplate, {
    onSuccess: (_data, variables) => {
      const savedSnapshot = variables.rcsConfigSnapshot;
      const currentConfig = rcsConfigRef.current;
      const stillMatches = savedSnapshot && JSON.stringify(savedSnapshot) === JSON.stringify(currentConfig);
      if (stillMatches) {
        dispatch({ type: 'SET_LAST_SAVED_RCS_CONFIG', payload: { rcsConfig: savedSnapshot } });
        dispatch({ type: 'SET_UNSAVED_CHANGES', payload: { hasUnsavedChanges: false } });
      }
      if (siteId && templateId) {
        queryClient.invalidateQueries(['template', siteId, templateId, variables.language]);
        if (routeLanguage && routeLanguage !== variables.language) {
          queryClient.invalidateQueries(['template', siteId, templateId, routeLanguage]);
        }
      }
      showNotification(t('designs:designGallery.templateEditor.editSuccess'), 'normal');
    },
    onError: (error: Error) => {
      showNotification(error.message ?? t('designs:designGallery.templateEditor.queries.save.error'), 'error');
    },
  });

  return { saveMutation, redirectUrl, history, siteId, templateId, routeLanguage };
}
